import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, useNavigationType } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Icon } from '@/components/ui/Icon';
import { SearchBar } from '@/components/ui/SearchBar';
import { FilterChip } from '@/components/ui/FilterChip';
import { LogoImage } from '@/components/ui/LogoImage';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { formatWeekendLabel, useMatchStore } from '@/stores/matchStore';
import { useProfileStore } from '@/stores/profileStore';
import { trackUsageEvent } from '@/services/usageEvent';

const FILTERS_CONTAINER_STYLE: React.CSSProperties = {
  display: 'flex',
  overflowX: 'auto',
  paddingTop: 12,
  paddingBottom: 12,
  gap: 0,
};

const FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'u14', label: 'U14' },
  { key: 'u15', label: 'U15' },
  { key: 'u15f', label: 'U15F' },
  { key: 'u16', label: 'U16' },
  { key: 'u17', label: 'U17' },
  { key: 'u18', label: 'U18' },
  { key: 'u18f', label: 'U18F' },
  { key: 'u19', label: 'U19' },
  { key: 'u20', label: 'U20' },
  { key: 'seniors', label: 'Seniors' },
  { key: 'r1', label: 'R1' },
  { key: 'r2', label: 'R2' },
  { key: 'r3', label: 'R3' },
  { key: 'national', label: 'NATIONAL' },
];

const DEFAULT_CENTER: [number, number] = [48.8566, 2.3522];

function createMarkerIcon(selected: boolean) {
  const color = selected ? Colors.accent.green : Colors.primary;
  return L.divIcon({
    className: '',
    html: `<div style="width:28px;height:28px;border-radius:14px;background:${color};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);${selected ? 'transform:scale(1.3);' : ''}"></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function MapController({ center, zoom }: { center?: [number, number]; zoom?: number }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom ?? 13, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (date.toDateString() === tomorrow.toDateString()) return 'Demain';
  return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function MapPage() {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const tifosiCle = useProfileStore((s) => s.tifosiCle);
  const [searchParams] = useSearchParams();
  const colors = useTheme();
  const {
    matches,
    fetchMatches,
    isLoading,
    currentFilter,
    searchQuery,
    setFilter,
    setSearchQuery,
    weekOffset,
    setWeekOffset,
    getFilteredMatches,
  } = useMatchStore();

  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [flyTo, setFlyTo] = useState<[number, number] | undefined>();
  const [flyZoom, setFlyZoom] = useState<number | undefined>();

  const baseFilteredMatches = getFilteredMatches();
  const filteredMatches = baseFilteredMatches.filter((m) => m.latitude && m.longitude);
  const selectedMatch = selectedMatchId ? matches.find((m) => m.idmatch_football === selectedMatchId) || null : null;

  useEffect(() => { fetchMatches(); }, []);

  useEffect(() => {
    if (!tifosiCle) return;
    trackUsageEvent(
      { type: 'map_opened', tifosi: tifosiCle },
      { navigationType, dedupeContext: 'map' },
    );
  }, [tifosiCle, navigationType]);

  useEffect(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const matchId = searchParams.get('match');
    if (lat && lng && matchId && matches.length) {
      setFlyTo([parseFloat(lat), parseFloat(lng)]);
      setFlyZoom(14);
      setSelectedMatchId(matchId);
    }
  }, [matches, searchParams]);

  const requestLocation = async () => {
    setIsLocating(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true })
      );
      const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
      setUserLocation(loc);
      setFlyTo([loc.latitude, loc.longitude]);
      setFlyZoom(12);
    } catch (e) {
      console.error('Location error:', e);
    } finally {
      setIsLocating(false);
    }
  };

  const openDirections = () => {
    if (selectedMatch?.latitude && selectedMatch?.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedMatch.latitude},${selectedMatch.longitude}`, '_blank');
    }
  };

  const handleRefresh = useCallback(() => {
    void fetchMatches(true);
  }, [fetchMatches]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
      <div style={{ flexShrink: 0, zIndex: 20, backgroundColor: colors.background }}>
        <div style={{ padding: '8px 16px 8px 16px', borderBottom: `1px solid ${Colors.primary}15` }}>
          <div style={styles.searchRow}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SearchBar value={searchQuery} onChangeText={setSearchQuery} placeholder="Rechercher clubs ou stades..." />
            </div>
            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
              <button
                type="button"
                onClick={handleRefresh}
                aria-label="Actualiser"
                style={{ ...styles.iconButton, flexShrink: 0, backgroundColor: colors.surface, border: 'none', cursor: 'pointer' }}
              >
                <Icon name="refresh" size={22} color={colors.text} />
              </button>
            </div>
          </div>
          <div style={FILTERS_CONTAINER_STYLE}>
            {FILTERS.map((f) => (
              <FilterChip key={f.key} label={f.label} isActive={currentFilter === f.key} onPress={() => setFilter(f.key)} />
            ))}
          </div>
        </div>

        <div style={{ padding: '12px 16px' }}>
          <div style={styles.weekNavRow}>
            <button
              type="button"
              onClick={() => setWeekOffset(-1)}
              style={{ ...styles.weekNavButton, backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <Icon name="chevron-back" size={20} color={colors.text} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, flex: 1, textAlign: 'center', color: colors.text }}>
              {formatWeekendLabel(weekOffset)}
            </span>
            <button
              type="button"
              onClick={() => setWeekOffset(1)}
              style={{ ...styles.weekNavButton, backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <Icon name="chevron-forward" size={20} color={colors.text} />
            </button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {isLoading && (
          <div style={styles.loadingOverlay}>
            <div className="spinner" />
            <div style={{ color: '#ffffff', marginTop: 12, fontSize: 14 }}>Chargement des matchs...</div>
          </div>
        )}

        <MapContainer center={DEFAULT_CENTER} zoom={10} style={{ width: '100%', height: '100%' }} zoomControl={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>' />
          {flyTo && <MapController center={flyTo} zoom={flyZoom} />}
          {filteredMatches.map((match) => {
            const lat = parseFloat(match.latitude!);
            const lng = parseFloat(match.longitude!);
            if (isNaN(lat) || isNaN(lng)) return null;
            return (
              <Marker
                key={match.idmatch_football}
                position={[lat, lng]}
                icon={createMarkerIcon(selectedMatchId === match.idmatch_football)}
                eventHandlers={{ click: () => setSelectedMatchId(match.idmatch_football) }}
              >
                <Popup>{match.homeTeam} vs {match.awayTeam}</Popup>
              </Marker>
            );
          })}
        </MapContainer>

        <div style={styles.mapControls}>
          <button onClick={requestLocation} style={{ ...styles.controlBtn, backgroundColor: colors.background, borderColor: colors.border }}>
            {isLocating ? <div className="spinner spinner-small" /> : <Icon name="locate" size={24} color={Colors.primary} />}
          </button>
        </div>

        <div style={{ ...styles.matchCount, backgroundColor: colors.background, borderColor: colors.border }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>{filteredMatches.length} matchs</span>
        </div>

        {selectedMatch && (
          <div style={{ ...styles.previewCard, backgroundColor: colors.background, borderColor: colors.border }}>
            <button onClick={() => setSelectedMatchId(null)} style={styles.closeButton}>
              <div style={{ ...styles.closeButtonInner, backgroundColor: colors.surface }}>
                <Icon name="close" size={18} color={colors.text} />
              </div>
            </button>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <LogoImage uri={selectedMatch.home_logo} size={40} />
                <span style={{ fontSize: 10, fontWeight: 700, textAlign: 'center', color: colors.text }}>{selectedMatch.homeTeam}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 8px' }}>
                <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: Colors.primary }}>{selectedMatch.competition}</span>
                <span style={{ fontSize: 20, fontWeight: 900, color: colors.text }}>{selectedMatch.heure?.slice(0, 5) || '--:--'}</span>
                <span style={{ fontSize: 10, color: colors.textSecondary }}>{formatDate(selectedMatch.date)}</span>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <LogoImage uri={selectedMatch.away_logo} size={40} />
                <span style={{ fontSize: 10, fontWeight: 700, textAlign: 'center', color: colors.text }}>{selectedMatch.awayTeam}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12 }}>
              <Icon name="location-outline" size={14} color={colors.textSecondary} />
              <span style={{ fontSize: 12, flex: 1, color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[selectedMatch.nom || selectedMatch.terrain, selectedMatch.ville].filter(Boolean).join(', ')}
              </span>
              {userLocation && selectedMatch.latitude && selectedMatch.longitude && (
                <span style={{ fontSize: 12, fontWeight: 600, color: Colors.primary }}>
                  {calculateDistance(userLocation.latitude, userLocation.longitude, parseFloat(selectedMatch.latitude), parseFloat(selectedMatch.longitude)).toFixed(1)} km
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => navigate(`/match/${selectedMatch.idmatch_football}`)} style={styles.previewMainButton}>
                <span style={{ color: '#ffffff', fontSize: 14, fontWeight: 700 }}>Voir détails</span>
              </button>
              <button onClick={() => navigate(`/fanzone/${selectedMatch.idmatch_football}`)} style={{ ...styles.previewSecondaryButton, backgroundColor: colors.surface }}>
                <Icon name="chatbubbles" size={20} color={Colors.primary} />
              </button>
              <button onClick={openDirections} style={{ ...styles.previewSecondaryButton, backgroundColor: colors.surface }}>
                <Icon name="navigate" size={20} color={Colors.primary} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  searchRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    paddingTop: 4,
    paddingBottom: 4,
  },
  iconButton: { width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  weekNavRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 },
  weekNavButton: {
    width: 36, height: 36, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid', cursor: 'pointer',
  },
  loadingOverlay: { position: 'absolute', inset: 0, backgroundColor: 'rgba(33,17,17,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 20 },
  mapControls: { position: 'absolute', right: 12, top: 12, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 8 },
  controlBtn: { width: 40, height: 40, borderRadius: 12, border: '1px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  matchCount: { position: 'absolute', left: 12, top: 12, paddingLeft: 12, paddingRight: 12, paddingTop: 6, paddingBottom: 6, borderRadius: 16, border: '1px solid', zIndex: 10 },
  previewCard: { position: 'absolute', left: 12, right: 12, bottom: 12, borderRadius: 16, border: '1px solid', padding: 12, zIndex: 10 },
  closeButton: { position: 'absolute', top: -12, right: -6, zIndex: 10, padding: 4, cursor: 'pointer' },
  closeButtonInner: { width: 28, height: 28, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,.2)' },
  previewMainButton: { flex: 1, backgroundColor: Colors.primary, paddingTop: 10, paddingBottom: 10, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  previewSecondaryButton: { width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
};
